import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Toaster, toast } from 'react-hot-toast';
import LazyImage from './components/LazyImage';
import { 
  Phone, 
  MessageCircle, 
  MapPin, 
  Clock, 
  Star, 
  ShoppingCart, 
  Truck, 
  Users, 
  ChefHat,
  Menu,
  X,
  Mail,
  ExternalLink,
  Plus,
  Minus,
  Trash2,
  Award,
  Zap,
  Heart,
  Timer,
  CheckCircle,
  ArrowRight,
  Gift,
  TrendingUp,
  User,
  AlertCircle,
  Shield,
  FileText,
  Cookie
} from 'lucide-react';
import './App.css';

// Import local hero image
import heroImage from './image/hero.jpg';

// Assets URLs
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_1f84c983-7ef0-4616-a81b-f0ca8689b38f/artifacts/gtnb0agp_logo1.png';

// Images from vision expert
const ABOUT_IMAGE = 'https://images.unsplash.com/photo-1519167029065-04732af94353?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxuaWdodCUyMHJlc3RhdXJhbnR8ZW58MHx8fHJlZHwxNzUzNjUxMjU3fDA&ixlib=rb-4.1.0&q=85';
const FOOTER_IMAGE = 'https://images.unsplash.com/photo-1585554934973-a0972837fb45?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxuaWdodCUyMHJlc3RhdXJhbnR8ZW58MHx8fHJlZHwxNzUzNjUxMjU3fDA&ixlib=rb-4.1.0&q=85';

// Fallback image pour les images manquantes
const FALLBACK_IMAGE = '/placeholder-food.jpg';

// Static imports for all images to avoid webpack warnings
// Burgers
import avocadoImg from './image/Burger/Avocado.jpg';
import avocadoMenuImg from './image/Burger/Avocadomenu.jpg';
import britishImg from './image/Burger/british.jpg';
import britishMenuImg from './image/Burger/british menu.jpg';
import cheeseImg from './image/Burger/cheese (2).jpg';
import classikImg from './image/Burger/classik.jpg';
import classikMenuImg from './image/Burger/classik menu.jpg';
import chiknImg from './image/Burger/chikn.jpg';
import chiknMenuImg from './image/Burger/Chiknmenu.jpg';
import chevreMielImg from './image/Burger/Cmiel.jpg';
import chevreMielMenuImg from './image/Burger/Cmiel menu.jpg';
import texasImg from './image/Burger/texas.jpg';
import texasMenuImg from './image/Burger/Texsasmenu.jpg';
import tripleSoImg from './image/Burger/Tripleso.jpg';
import tripleSoMenuImg from './image/Burger/Triplesomenu.jpg';

// Sandwiches
import americainImg from './image/Sandwich/americain.jpg';
import americainMenuImg from './image/Sandwich/americain menu.jpg';
import boursinImg from './image/Sandwich/Boursin.jpg';
import boursinMenuImg from './image/Sandwich/Boursin menu.jpg';
import british2Img from './image/Sandwich/British (2).jpg';
import britishSandwichImg from './image/Sandwich/British.png';
import chikenCheeseImg from './image/Sandwich/chiken cheese.jpg';
import chikenCheeseMenuImg from './image/Sandwich/chiken cheese menu.jpg';
import soCurryImg from './image/Sandwich/curry.jpg';
import soCurryMenuImg from './image/Sandwich/curry menu.jpg';
import tandoyaImg from './image/Sandwich/tandori.jpg';
import tandoyaMenuImg from './image/Sandwich/tandori menu.jpg';

// Autres
import nuggetsImg from './image/Autres/Nuggets.jpg';
import stickMozzaImg from './image/Autres/Stick mozza.jpg';
import tendersImg from './image/Autres/tenders.jpg';
import wrapImg from './image/Autres/Wrap.jpg';

// Remove the loadImage function and replace with static object
const PRODUCT_IMAGES = {
  // Burgers
  avocado: avocadoImg,
  avocado_menu: avocadoMenuImg,
  british: britishImg,
  british_menu: britishMenuImg,
  cheese: cheeseImg,
  cheese_menu: classikMenuImg,
  classik: classikImg,
  classik_menu: classikMenuImg,
  chikn: chiknImg,
  chikn_menu: chiknMenuImg,
  chevre_miel: chevreMielImg,
  chevre_miel_menu: chevreMielMenuImg,
  texas: texasImg,
  texas_menu: texasMenuImg,
  triple_so: tripleSoImg,
  triple_so_menu: tripleSoMenuImg,
  
  // Sandwiches
  americain: americainImg,
  americain_menu: americainMenuImg,
  boursin: boursinImg,
  boursin_menu: boursinMenuImg,
  british2: british2Img,
  british_sandwich: britishSandwichImg,
  chiken_cheese: chikenCheeseImg,
  chiken_cheese_menu: chikenCheeseMenuImg,
  so_curry: soCurryImg,
  so_curry_menu: soCurryMenuImg,
  tandoya: tandoyaImg,
  tandoya_menu: tandoyaMenuImg,
  
  // Autres
  nuggets: nuggetsImg,
  stick_mozza: stickMozzaImg,
  tenders: tendersImg,
  wrap: wrapImg,
  
  // Fallback for missing images
  frites_cheddar: FALLBACK_IMAGE,
};

// Sauces disponibles
const SAUCES = [
  { id: 'sans-sauce', name: 'Sans sauce', price: 0 },
  { id: 'algerienne', name: 'Algérienne', price: 0, popular: true },
  { id: 'mayo', name: 'Mayo', price: 0 },
  { id: 'ketchup', name: 'Ketchup', price: 0 },
  { id: 'sauce-smash', name: 'Sauce Smash', price: 0, popular: true },
  { id: 'barbecue', name: 'Barbecue', price: 0 },
  { id: 'biggy', name: 'Biggy', price: 0 }
];

// Boissons disponibles
const BOISSONS = [
  { id: 'coca', name: 'Coca-Cola', price: 2.50, category: 'boissons' },
  { id: 'fanta', name: 'Fanta Orange', price: 2.50, category: 'boissons' },
  { id: 'sprite', name: 'Sprite', price: 2.50, category: 'boissons' },
  { id: 'miranda', name: 'Miranda', price: 2.50, category: 'boissons' },
  { id: 'tropico', name: 'Tropico', price: 2.50, category: 'boissons' },
  { id: 'orangina', name: 'Orangina', price: 2.50, category: 'boissons' },
  { id: 'ice-tea', name: 'Ice Tea Pêche', price: 2.50, category: 'boissons' },
  { id: 'caprisun', name: 'Capri Sun', price: 2.00, category: 'boissons' },
  { id: 'eau', name: 'Eau 50cl', price: 1.50, category: 'boissons' },
  { id: 'eau-gazeuse', name: 'Eau gazeuse 50cl', price: 1.80, category: 'boissons' }
];

// Menu Data with real images and enhancements
const menuItems = {
  burgers: [
    { 
      id: 'burger1',
      name: 'CLASSIK\'SO', 
      price: 7.90, 
      originalPrice: 8.90,
      description: 'Steak x2, salade, tomate, oignons, sauce inclue, cornichon, cheddar',
      image: PRODUCT_IMAGES.classik,
      menuImage: PRODUCT_IMAGES.classik_menu,
      popular: true,
      spicy: false,
      category: 'burgers'
    },
    { 
      id: 'burger2',
      name: 'SOYA AVOCADO', 
      price: 8.90, 
      description: 'Escalope panée, cheddar, avocat, tomate, sauce inclue',
      image: PRODUCT_IMAGES.avocado,
      menuImage: PRODUCT_IMAGES.avocado_menu,
      popular: false,
      spicy: false,
      new: true,
      category: 'burgers'
    },
    { 
      id: 'burger3',
      name: 'LE TEXAS', 
      price: 8.90, 
      description: 'Steak x2, salade, tomate, oignons caramélisés, bacon, cheddar, sauce inclue',
      image: PRODUCT_IMAGES.texas,
      menuImage: PRODUCT_IMAGES.texas_menu,
      popular: false,
      spicy: true,
      category: 'burgers'
    },
    { 
      id: 'burger4',
      name: 'CHIK\'N', 
      price: 7.90, 
      description: 'Escalope panée, cheddar, cornichon, salade, oignons frits, sauce inclue',
      image: PRODUCT_IMAGES.chikn,
      menuImage: PRODUCT_IMAGES.chikn_menu,
      popular: true,
      spicy: false,
      category: 'burgers'
    },
    { 
      id: 'burger5',
      name: 'CHÈVRE MIEL', 
      price: 8.90, 
      description: 'Steak x2, chèvre miel, oignons frits, salade, sauce inclue',
      image: PRODUCT_IMAGES.chevre_miel,
      menuImage: PRODUCT_IMAGES.chevre_miel_menu,
      popular: false,
      spicy: false,
      category: 'burgers'
    },
    { 
      id: 'burger6',
      name: 'TRIPLE SO', 
      price: 8.90, 
      description: 'Steak x3, cheddar, salade, tomate, oignons, cornichon, sauce inclue',
      image: PRODUCT_IMAGES.triple_so,
      menuImage: PRODUCT_IMAGES.triple_so_menu,
      popular: false,
      spicy: false,
      category: 'burgers'
    },
    { 
      id: 'burger7',
      name: 'BRITISH', 
      price: 9.90, 
      description: 'Steak x2, oeuf, bacon, cheddar, salade, tomate, oignons, sauce inclue',
      image: PRODUCT_IMAGES.british,
      menuImage: PRODUCT_IMAGES.british_menu,
      popular: true,
      spicy: false,
      category: 'burgers'
    }
  ],
  sandwiches: [
    { 
      id: 'sandwich1',
      name: 'L\'AMÉRICAIN', 
      price: 8.90, 
      description: 'Steak smashé, cheddar, salade, tomate, oignons, sauce inclue',
      image: PRODUCT_IMAGES.americain,
      menuImage: PRODUCT_IMAGES.americain_menu,
      popular: true,
      spicy: false,
      category: 'sandwiches'
    },
    { 
      id: 'sandwich2',
      name: 'SO\'CURRY', 
      price: 8.90, 
      description: 'Poulet mariné curry, cheddar, salade, tomates, oignons, sauce inclue',
      image: PRODUCT_IMAGES.so_curry,
      menuImage: PRODUCT_IMAGES.so_curry_menu,
      popular: false,
      spicy: true,
      category: 'sandwiches'
    },
    { 
      id: 'sandwich3',
      name: 'TANDO\'YA', 
      price: 8.90, 
      description: 'Poulet mariné tandoori, cheddar, salade, tomates, oignons, sauce inclue',
      image: PRODUCT_IMAGES.tandoya,
      menuImage: PRODUCT_IMAGES.tandoya_menu,
      popular: false,
      spicy: true,
      category: 'sandwiches'
    },
    { 
      id: 'sandwich4',
      name: 'CHIK\'N BEEF', 
      price: 9.90, 
      description: 'Steak smashé, cheddar, tenders, salade, tomate, oignons, sauce inclue',
      image: PRODUCT_IMAGES.chiken_cheese,
      menuImage: PRODUCT_IMAGES.chiken_cheese_menu,
      popular: true,
      spicy: false,
      category: 'sandwiches'
    },
    { 
      id: 'sandwich5',
      name: 'BOURSIN', 
      price: 8.90, 
      description: 'Poulet, cheddar, boursin, salade, tomate, oignons, sauce inclue',
      image: PRODUCT_IMAGES.boursin,
      menuImage: PRODUCT_IMAGES.boursin_menu,
      popular: false,
      spicy: false,
      category: 'sandwiches'
    },
    { 
      id: 'sandwich6',
      name: 'BRITISH SANDWICH', 
      price: 9.90, 
      description: 'Steak smashé, cheddar, bacon, oeuf, salade, tomate, oignons, sauce inclue',
      image: PRODUCT_IMAGES.british_sandwich,
      menuImage: PRODUCT_IMAGES.british2,
      popular: false,
      spicy: false,
      category: 'sandwiches'
    }
  ],
  kids: [
    { 
      id: 'kids1',
      name: 'MENU ENFANT', 
      price: 7.90, 
      description: 'Un cheese burger ou 5 nuggets + frites + caprisun',
      image: PRODUCT_IMAGES.nuggets,
      popular: true,
      spicy: false,
      category: 'kids'
    }
  ],
  petiteFaim: [
    { 
      id: 'snack1',
      name: 'CHEESEBURGER', 
      price: 3.50,
      description: 'Petit burger avec fromage',
      image: PRODUCT_IMAGES.cheese,
      popular: false,
      spicy: false,
      category: 'petiteFaim'
    },
    { 
      id: 'snack2',
      name: 'PETIT WRAP', 
      price: 3.50,
      description: 'Wrap poulet cheddar',
      image: PRODUCT_IMAGES.wrap,
      popular: false,
      spicy: false,
      category: 'petiteFaim'
    },
    { 
      id: 'snack3',
      name: 'STICK MOZZA X5', 
      price: 3.90,
      description: '5 sticks mozzarella croustillants',
      image: PRODUCT_IMAGES.stick_mozza,
      popular: true,
      spicy: false,
      category: 'petiteFaim'
    },
    { 
      id: 'snack4',
      name: 'NUGGET X5', 
      price: 3.90,
      description: '5 nuggets de poulet croustillants',
      image: PRODUCT_IMAGES.nuggets,
      popular: true,
      spicy: false,
      category: 'petiteFaim'
    },
    { 
      id: 'snack5',
      name: 'TENDERS X4', 
      price: 4.00,
      description: '4 tenders de poulet marinés',
      image: PRODUCT_IMAGES.tenders,
      popular: false,
      spicy: false,
      category: 'petiteFaim'
    }
  ],
  sides: [
    { 
      id: 'side1',
      name: 'FRITES CHEDDAR', 
      price: 4.50,
      description: 'Frites dorées avec cheddar fondu',
      image: PRODUCT_IMAGES.frites_cheddar,
      popular: true,
      spicy: false,
      category: 'sides'
    },
    { 
      id: 'side2',
      name: 'FRITES CHEDDAR BACON', 
      price: 4.50,
      description: 'Frites dorées avec cheddar et bacon',
      image: PRODUCT_IMAGES.frites_cheddar,
      popular: true,
      spicy: false,
      category: 'sides'
    }
  ],
  boissons: BOISSONS
};
// --- Validation Utils ---
const ValidationUtils = {
  validateName: (name) => /^[A-Za-zÀ-ÿ '-]{2,50}$/.test(name.trim()),
  validatePhone: (phone) => /^0[1-9](?:[\s.-]?\d{2}){4}$/.test(phone.trim()),
  validateAddress: (address) => address.trim().length >= 10 && address.trim().length <= 200,
  validateNotes: (notes) => notes.length <= 500,
  sanitizeInput: (value) => value.replace(/[<>]/g, '')
};

// --- CONTEXTE PANIER ---
const CartContext = React.createContext();

const getCartFromStorage = () => {
  try {
    const data = localStorage.getItem('soya-cart');
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [storageAvailable, setStorageAvailable] = useState(true);
  const [error, setError] = useState(null);

  // Vérifie la disponibilité du localStorage
  useEffect(() => {
    try {
      localStorage.setItem('test', 'test');
      localStorage.removeItem('test');
      setStorageAvailable(true);
    } catch {
      setStorageAvailable(false);
      setError('Le stockage local n\'est pas disponible. Votre panier sera perdu au rechargement.');
      toast.error('Stockage désactivé - Panier temporaire', {
        icon: '⚠️',
        duration: 5000,
        style: { background: '#1a1a1a', color: '#fff', border: '1px solid #ef4444' }
      });
    }
  }, []);

  // Charge le panier au démarrage
  useEffect(() => {
    if (storageAvailable) {
      setCart(getCartFromStorage());
    }
  }, [storageAvailable]);

  // Sauvegarde le panier à chaque modification
  useEffect(() => {
    if (storageAvailable) {
      try {
        localStorage.setItem('soya-cart', JSON.stringify(cart));
      } catch {
        setError('Impossible de sauvegarder le panier');
      }
    }
  }, [cart, storageAvailable]);

  // Ajoute un produit (avec gestion des personnalisations)
  const addToCart = (item) => {
    setCart(prev => {
      // Si l'item a une personnalisation, on le considère toujours unique
      if (item.customization) {
        return [...prev, { ...item, quantity: 1 }];
      }
      // Sinon, on regroupe par id
      const existing = prev.find(i => i.id === item.id && !i.customization);
      if (existing) {
        return prev.map(i =>
          i.id === item.id && !i.customization
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      return [...prev, { ...item, quantity: 1 }];
    });
    toast.success(`${item.name} ajouté au panier!`, {
      duration: 2000,
      icon: '🛒',
      style: { background: '#1a1a1a', color: '#fff', border: '1px solid #E91E63' }
    });
  };

  const removeFromCart = (itemId) => setCart(prev => prev.filter(item => item.id !== itemId));
  const updateQuantity = (itemId, quantity) => setCart(prev =>
    prev.map(item => item.id === itemId ? { ...item, quantity: Math.max(1, quantity) } : item)
  );
  const clearCart = () => {
    setCart([]);
    if (storageAvailable) localStorage.removeItem('soya-cart');
  };
  const getCartTotal = () => cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  const getCartCount = () => cart.reduce((count, item) => count + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart, addToCart, removeFromCart, updateQuantity, clearCart,
      getCartTotal, getCartCount, isCartOpen, setIsCartOpen, storageAvailable, error
    }}>
      {children}
    </CartContext.Provider>
  );
};
const useCart = () => {
  const context = React.useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};


// Components
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { getCartCount, setIsCartOpen } = useCart();

  return (
    <motion.header 
      className="fixed top-0 left-0 right-0 z-50 glass-effect"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <motion.div
            className="flex items-center space-x-3"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <img src={LOGO_URL} alt="Soya-Food" className="h-12 w-12" />
            <div>
              <h1 className="text-xl font-bold text-soya-yellow">Soya-Food</h1>
              <p className="text-sm text-gray-300">Snack de nuit • Cugnaux</p>
            </div>
          </motion.div>

          <nav className="hidden md:flex items-center space-x-6">
            <a href="#accueil" className="text-white hover:text-soya-yellow transition-colors">Accueil</a>
            <a href="#menu" className="text-white hover:text-soya-yellow transition-colors">Menu</a>
            <a href="#livraison" className="text-white hover:text-soya-yellow transition-colors">Livraison</a>
            <a href="#contact" className="text-white hover:text-soya-yellow transition-colors">Contact</a>
            
            <motion.button
              onClick={() => setIsCartOpen(true)}
              className="relative bg-soya-red hover:bg-soya-red/80 px-4 py-2 rounded-full text-white font-semibold flex items-center space-x-2 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ShoppingCart size={18} />
              <span>Panier</span>
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-soya-yellow text-black text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
                  {getCartCount()}
                </span>
              )}
            </motion.button>
          </nav>

          <div className="md:hidden flex items-center space-x-4">
            <motion.button
              onClick={() => setIsCartOpen(true)}
              className="relative bg-soya-red hover:bg-soya-red/80 p-2 rounded-full text-white"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ShoppingCart size={20} />
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-soya-yellow text-black text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                  {getCartCount()}
                </span>
              )}
            </motion.button>

            <button
              className="text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            className="md:hidden mt-4 py-4 border-t border-gray-700"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <nav className="flex flex-col space-y-3">
              <a href="#accueil" className="text-white hover:text-soya-yellow transition-colors">Accueil</a>
              <a href="#menu" className="text-white hover:text-soya-yellow transition-colors">Menu</a>
              <a href="#livraison" className="text-white hover:text-soya-yellow transition-colors">Livraison</a>
              <a href="#contact" className="text-white hover:text-soya-yellow transition-colors">Contact</a>
            </nav>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

const Cart = () => {
  const { cart, isCartOpen, setIsCartOpen, removeFromCart, updateQuantity, getCartTotal, clearCart } = useCart();
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    address: '',
    delivery: true,
    notes: ''
  });
  // Fonctions de validation simples
const validateName = (name) => /^[A-Za-zÀ-ÿ '-]{2,50}$/.test(name.trim());
const validatePhone = (phone) => /^0[1-9](?:[\s.-]?\d{2}){4}$/.test(phone.trim());
const validateAddress = (address) => address.trim().length >= 10 && address.trim().length <= 200;
const validateNotes = (notes) => notes.length <= 500;
const sanitizeInput = (value) => value.replace(/[<>]/g, '');
  const [validationErrors, setValidationErrors] = useState({});

  // Validation en temps réel sécurisée
  const validateForm = () => {
    const errors = {};

    if (!ValidationUtils.validateName(customerInfo.name)) {
      errors.name = 'Nom invalide (2-50 caractères, lettres uniquement)';
    }

    if (!ValidationUtils.validatePhone(customerInfo.phone)) {
      errors.phone = 'Numéro de téléphone français invalide';
    }

    if (customerInfo.delivery && !ValidationUtils.validateAddress(customerInfo.address)) {
      errors.address = 'Adresse invalide (10-200 caractères)';
    }

    if (!ValidationUtils.validateNotes(customerInfo.notes)) {
      errors.notes = 'Notes trop longues (max 500 caractères)';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    const sanitizedValue = ValidationUtils.sanitizeInput(value);
    setCustomerInfo(prev => ({ ...prev, [field]: sanitizedValue }));
    
    // Validation en temps réel
    if (validationErrors[field]) {
      const newErrors = { ...validationErrors };
      delete newErrors[field];
      setValidationErrors(newErrors);
    }
  };

  const handleWhatsAppOrder = () => {
    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs dans le formulaire', {
        icon: '⚠️',
        style: {
          background: '#1a1a1a',
          color: '#fff',
          border: '1px solid #ef4444'
        }
      });
      return;
    }

    // Sécuriser les données avant envoi
    const secureOrderDetails = cart.map(item => {
      const sanitizedName = ValidationUtils.sanitizeInput(item.name);
      let itemText = `• ${sanitizedName} x${item.quantity} = ${(item.price * item.quantity).toFixed(2)}€`;
      
      if (item.customization) {
        const { sauce, extraSauces, isMenu, boisson } = item.customization;
        let customDetails = [];
        
        if (sauce?.name) {
          customDetails.push(`Sauce: ${ValidationUtils.sanitizeInput(sauce.name)}`);
        }
        
        if (extraSauces?.length > 0) {
          const sanitizedSauces = extraSauces.map(s => ValidationUtils.sanitizeInput(s.name)).join(', ');
          customDetails.push(`Sauces extra: ${sanitizedSauces}`);
        }
        
        if (isMenu && boisson?.name) {
          customDetails.push(`Menu: Frites + ${ValidationUtils.sanitizeInput(boisson.name)}`);
        }
        
        if (customDetails.length > 0) {
          itemText += `\n  ${customDetails.join(' | ')}`;
        }
      }
      
      return itemText;
    }).join('\n');

    const deliveryFee = customerInfo.delivery ? 5.00 : 0;
    const total = getCartTotal() + deliveryFee;

    const secureMessage = `🍔 NOUVELLE COMMANDE SOYA-FOOD 🍔

👤 Client: ${ValidationUtils.sanitizeInput(customerInfo.name)}
📞 Téléphone: ${ValidationUtils.sanitizeInput(customerInfo.phone)}
${customerInfo.delivery ? `🚚 Livraison: ${ValidationUtils.sanitizeInput(customerInfo.address)}` : '🏪 À emporter'}

📋 COMMANDE:
${secureOrderDetails}

💰 Sous-total: ${getCartTotal().toFixed(2)}€
${customerInfo.delivery ? `🚚 Livraison: ${deliveryFee.toFixed(2)}€` : ''}
💳 TOTAL: ${total.toFixed(2)}€

${customerInfo.notes ? `📝 Notes: ${ValidationUtils.sanitizeInput(customerInfo.notes)}` : ''}

Merci pour votre commande ! 🙏`;

    const whatsappUrl = `https://wa.me/33618364907?text=${encodeURIComponent(secureMessage)}`;
    window.open(whatsappUrl, '_blank');
    
    toast.success('Commande envoyée via WhatsApp!', {
      icon: '✅',
      duration: 3000,
      style: {
        background: '#1a1a1a',
        color: '#fff',
        border: '1px solid #25D366'
      }
    });
    
    clearCart();
    setIsCartOpen(false);
  };

  return (
    <AnimatePresence>
      {isCartOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsCartOpen(false)} />
          
          <motion.div
            className="relative glass-effect rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-soya-yellow flex items-center">
                <ShoppingCart className="mr-2" size={24} />
                Mon Panier
              </h2>
              <button
                onClick={() => setIsCartOpen(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {cart.length === 0 ? (
              <div className="text-center py-8">
                <ShoppingCart className="mx-auto mb-4 text-gray-400" size={48} />
                <p className="text-gray-400">Votre panier est vide</p>
                <button
                  onClick={() => setIsCartOpen(false)}
                  className="mt-4 cta-button px-6 py-2 rounded-full text-black font-semibold"
                >
                  Continuer les achats
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Cart Items avec LazyImage */}
                <div className="space-y-3">
                  {cart.map(item => (
                    <div key={item.id} className="flex items-center space-x-3 bg-gray-800 rounded-lg p-3">
                      <LazyImage 
                        src={item.image} 
                        alt={item.name} 
                        className="w-12 h-12 object-cover rounded" 
                      />
                      <div className="flex-1">
                        <h4 className="text-white font-semibold text-sm">{item.name}</h4>
                        {item.customization && (
                          <div className="text-xs text-gray-400 mt-1">
                            <div>Sauce: {item.customization.sauce.name}</div>
                            {item.customization.extraSauces.length > 0 && (
                              <div>Extra: {item.customization.extraSauces.map(s => s.name).join(', ')}</div>
                            )}
                            {item.customization.isMenu && item.customization.boisson && (
                              <div>Menu: Frites + {item.customization.boisson.name}</div>
                            )}
                          </div>
                        )}
                        <p className="text-soya-red font-bold">{item.price.toFixed(2)}€</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="bg-gray-700 text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-600 transition-colors"
                        >
                          <Minus size={16} />
                        </button>
                        <span className="text-white font-semibold w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="bg-soya-red text-white w-8 h-8 rounded-full flex items-center justify-center hover:bg-soya-red/80 transition-colors"
                        >
                          <Plus size={16} />
                        </button>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="text-gray-400 hover:text-red-500 transition-colors ml-2"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Customer Information avec validation */}
                <div className="border-t border-gray-700 pt-4">
                  <h3 className="text-lg font-semibold text-white mb-3">Informations de livraison</h3>
                  <div className="space-y-3">
                    <div>
                      <input
                        type="text"
                        placeholder="Nom complet*"
                        value={customerInfo.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className={`w-full bg-gray-700 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                          validationErrors.name ? 'focus:ring-red-500 border border-red-500' : 'focus:ring-soya-red'
                        }`}
                        maxLength={50}
                      />
                      {validationErrors.name && (
                        <p className="text-red-400 text-xs mt-1 flex items-center">
                          <AlertCircle size={12} className="mr-1" />
                          {validationErrors.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <input
                        type="tel"
                        placeholder="Téléphone* (ex: 06 12 34 56 78)"
                        value={customerInfo.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        className={`w-full bg-gray-700 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                          validationErrors.phone ? 'focus:ring-red-500 border border-red-500' : 'focus:ring-soya-red'
                        }`}
                        maxLength={14}
                      />
                      {validationErrors.phone && (
                        <p className="text-red-400 text-xs mt-1 flex items-center">
                          <AlertCircle size={12} className="mr-1" />
                          {validationErrors.phone}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2 text-white">
                        <input
                          type="radio"
                          name="delivery"
                          checked={customerInfo.delivery}
                          onChange={() => setCustomerInfo(prev => ({ ...prev, delivery: true }))}
                          className="text-soya-red focus:ring-soya-red"
                        />
                        <span>Livraison (+5€)</span>
                      </label>
                      <label className="flex items-center space-x-2 text-white">
                        <input
                          type="radio"
                          name="delivery"
                          checked={!customerInfo.delivery}
                          onChange={() => setCustomerInfo(prev => ({ ...prev, delivery: false }))}
                          className="text-soya-red focus:ring-soya-red"
                        />
                        <span>À emporter</span>
                      </label>
                    </div>

                    {customerInfo.delivery && (
                      <div>
                        <input
                          type="text"
                          placeholder="Adresse de livraison complète*"
                          value={customerInfo.address}
                          onChange={(e) => handleInputChange('address', e.target.value)}
                          className={`w-full bg-gray-700 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 ${
                            validationErrors.address ? 'focus:ring-red-500 border border-red-500' : 'focus:ring-soya-red'
                          }`}
                          maxLength={200}
                        />
                        {validationErrors.address && (
                          <p className="text-red-400 text-xs mt-1 flex items-center">
                            <AlertCircle size={12} className="mr-1" />
                            {validationErrors.address}
                          </p>
                        )}
                      </div>
                    )}

                    <div>
                      <textarea
                        placeholder="Notes spéciales (optionnel)"
                        value={customerInfo.notes}
                        onChange={(e) => handleInputChange('notes', e.target.value)}
                        className={`w-full bg-gray-700 text-white px-3 py-2 rounded-lg focus:outline-none focus:ring-2 h-20 resize-none ${
                          validationErrors.notes ? 'focus:ring-red-500 border border-red-500' : 'focus:ring-soya-red'
                        }`}
                        maxLength={500}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {validationErrors.notes && (
                          <p className="text-red-400 text-xs flex items-center">
                            <AlertCircle size={12} className="mr-1" />
                            {validationErrors.notes}
                          </p>
                        )}
                        <p className="text-gray-400 text-xs ml-auto">
                          {customerInfo.notes.length}/500
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="border-t border-gray-700 pt-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-white">
                      <span>Sous-total:</span>
                      <span>{getCartTotal().toFixed(2)}€</span>
                    </div>
                    {customerInfo.delivery && (
                      <div className="flex justify-between text-white">
                        <span>Livraison:</span>
                        <span>5.00€</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xl font-bold text-soya-yellow border-t border-gray-700 pt-2">
                      <span>Total:</span>
                      <span>{(getCartTotal() + (customerInfo.delivery ? 5 : 0)).toFixed(2)}€</span>
                    </div>
                  </div>
                </div>

                {/* Order Button */}
                <motion.button
                  onClick={handleWhatsAppOrder}
                  className="w-full whatsapp-button py-3 rounded-lg text-white font-semibold flex items-center justify-center space-x-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <MessageCircle size={20} />
                  <span>Commander via WhatsApp</span>
                </motion.button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const cookieConsent = localStorage.getItem('soya-cookie-consent');
    if (!cookieConsent) {
      setShowBanner(true);
    }
  }, []);

  const acceptCookies = () => {
    localStorage.setItem('soya-cookie-consent', 'accepted');
    setShowBanner(false);
    toast.success('Paramètres de cookies enregistrés', {
      icon: '🍪',
      style: {
        background: '#1a1a1a',
        color: '#fff',
        border: '1px solid #E91E63'
      }
    });
  };

  const rejectCookies = () => {
    localStorage.setItem('soya-cookie-consent', 'rejected');
    setShowBanner(false);
    toast.success('Préférences enregistrées', {
      icon: '✅',
      style: {
        background: '#1a1a1a',
        color: '#fff',
        border: '1px solid #E91E63'
      }
    });
  };

  if (!showBanner) return null;

  return (
    <motion.div
      className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900 border-t border-gray-700 p-4"
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container mx-auto flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
        <div className="flex items-center space-x-3">
          <Cookie className="text-soya-yellow" size={24} />
          <div>
            <p className="text-white text-sm">
              Nous utilisons des cookies pour améliorer votre expérience. 
              En continuant, vous acceptez notre <a href="#politique-confidentialite" className="text-soya-yellow hover:underline">politique de confidentialité</a>.
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={rejectCookies}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Refuser
          </button>
          <button
            onClick={acceptCookies}
            className="px-4 py-2 bg-soya-red text-white rounded-lg hover:bg-soya-red/80 transition-colors"
          >
            Accepter
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Legal Pages Components
const MentionsLegales = () => {
  return (
    <section className="py-20 bg-gray-900 min-h-screen">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-5xl font-display font-black text-white mb-4 neon-glow">
            Mentions Légales
          </h1>
        </motion.div>

        <div className="glass-effect rounded-2xl p-8 text-gray-300 space-y-6">
          <div>
            <h3 className="text-xl font-bold text-soya-yellow mb-3">1. Informations sur l'entreprise</h3>
            <p><strong>Dénomination sociale :</strong> Soya-Food Cugnaux</p>
            <p><strong>Adresse :</strong> 31bis Av. de Toulouse, 31270 Cugnaux</p>
            <p><strong>Téléphone :</strong> 05 67 06 29 16</p>
            <p><strong>Forme juridique :</strong> Entreprise individuelle</p>
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-soya-yellow mb-3">2. Directeur de publication</h3>
            <p>Le directeur de la publication du site est le gérant de l'établissement.</p>
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-soya-yellow mb-3">3. Hébergement</h3>
            <p>Ce site est hébergé par un prestataire externe.</p>
          </div>

          <div>
            <h3 className="text-xl font-bold text-soya-yellow mb-3">4. Propriété intellectuelle</h3>
            <p>L'ensemble de ce site relève de la législation française et internationale sur le droit d'auteur et la propriété intellectuelle.</p>
          </div>

          <div>
            <h3 className="text-xl font-bold text-soya-yellow mb-3">5. Données personnelles</h3>
            <p>Conformément au RGPD, vous disposez d'un droit d'accès, de rectification et de suppression des données vous concernant.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const PolitiqueConfidentialite = () => {
  return (
    <section className="py-20 bg-gray-900 min-h-screen">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-5xl font-display font-black text-white mb-4 neon-glow">
            Politique de Confidentialité
          </h1>
        </motion.div>

        <div className="glass-effect rounded-2xl p-8 text-gray-300 space-y-6">
          <div>
            <h3 className="text-xl font-bold text-soya-yellow mb-3">1. Collecte des données</h3>
            <p>Nous collectons uniquement les données nécessaires pour traiter vos commandes : nom, téléphone, adresse de livraison.</p>
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-soya-yellow mb-3">2. Utilisation des données</h3>
            <p>Vos données sont utilisées exclusivement pour :</p>
            <ul className="list-disc ml-6 mt-2">
              <li>Traiter et livrer vos commandes</li>
              <li>Vous contacter en cas de besoin</li>
              <li>Améliorer nos services</li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-soya-yellow mb-3">3. Cookies</h3>
            <p>Nous utilisons des cookies techniques pour le bon fonctionnement du panier. Aucun cookie de tracking n'est utilisé sans votre consentement.</p>
          </div>

          <div>
            <h3 className="text-xl font-bold text-soya-yellow mb-3">4. Vos droits</h3>
            <p>Vous disposez des droits suivants :</p>
            <ul className="list-disc ml-6 mt-2">
              <li>Droit d'accès à vos données</li>
              <li>Droit de rectification</li>
              <li>Droit à l'effacement</li>
              <li>Droit de portabilité</li>
            </ul>
            <p className="mt-2">Pour exercer ces droits, contactez-nous à : ssoya741@gmail.com</p>
          </div>

          <div>
            <h3 className="text-xl font-bold text-soya-yellow mb-3">5. Conservation des données</h3>
            <p>Vos données de commande sont conservées 3 ans maximum pour des raisons comptables et légales.</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// RGPD Component
const CGUSection = () => {
  return (
    <section id="cgu" className="py-20 bg-gray-900">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl font-display font-black text-white mb-4 neon-glow">
            Conditions Générales d'Utilisation
          </h2>
        </motion.div>

        <div className="glass-effect rounded-2xl p-8 text-gray-300 space-y-6">
          <div>
            <h3 className="text-xl font-bold text-soya-yellow mb-3">1. Informations légales</h3>
            <p>
              Soya-Food Cugnaux<br />
              31bis Av. de Toulouse, 31270 Cugnaux<br />
              Téléphone : 05 67 06 29 16<br />
            </p>
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-soya-yellow mb-3">2. Commandes</h3>
            <p>
              Les commandes peuvent être passées par téléphone au 05 67 06 29 16 ou via WhatsApp au 06 18 36 49 07. 
              Toute commande implique l'acceptation des présentes conditions générales.
            </p>
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-soya-yellow mb-3">3. Livraison</h3>
            <p>
              Nous livrons dans un rayon de proximité autour de Cugnaux. Les frais de livraison sont de 5€. 
              Les délais de livraison sont donnés à titre indicatif et peuvent varier selon les conditions.
            </p>
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-soya-yellow mb-3">4. Paiement</h3>
            <p>
              Le paiement s'effectue à la livraison en espèces ou par carte bancaire. 
              Les prix affichés sont en euros et incluent la TVA.
            </p>
          </div>
          
          <div>
            <h3 className="text-xl font-bold text-soya-yellow mb-3">5. Annulation</h3>
            <p>
              Les commandes peuvent être annulées jusqu'à 10 minutes après la confirmation. 
              Au-delà, aucune annulation ne sera possible.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const HeroSection = () => {
  const [timeLeft, setTimeLeft] = useState(3600); // 1 hour in seconds

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 3600);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <section id="accueil" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${heroImage})`,
          filter: 'brightness(0.3)'
        }}
      />
      <div className="absolute inset-0 bg-pattern" />
      
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        {/* Promotion Banner */}

        <motion.div
          className="float-animation mb-8"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
        >
          <img src={LOGO_URL} alt="Soya-Food" className="w-32 h-32 mx-auto mb-6" />
        </motion.div>

        <motion.h1
          className="text-6xl md:text-8xl font-display font-black text-white mb-6 neon-glow"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          SOYA-FOOD
        </motion.h1>

        <motion.p
          className="text-xl md:text-2xl text-soya-yellow font-semibold mb-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Snack de nuit • Cugnaux
        </motion.p>

        <motion.p
          className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          Découvrez nos burgers et sandwiches gourmets, préparés avec passion. 
          Livraison rapide autour de Cugnaux !
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <motion.a
            href="#menu"
            className="cta-button px-8 py-4 rounded-full text-black font-bold text-lg flex items-center justify-center space-x-3"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChefHat size={24} />
            <span>Je commande</span>
          </motion.a>

          <motion.a
              href="tel:+33567062916"
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-button px-8 py-4 rounded-full text-white font-bold text-lg flex items-center justify-center space-x-3 bg-pink-600 hover:bg-pink-700 transition-colors duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Phone size={24} />
              <span>Nous appeler</span>
          </motion.a>

        </motion.div>

        <motion.div
          className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <div className="glass-effect p-4 rounded-xl">
            <Clock className="text-soya-yellow mx-auto mb-2" size={24} />
            <h3 className="text-white font-semibold">Ouvert tard</h3>
            <p className="text-gray-300 text-sm">Jusqu'à 2h du matin</p>
          </div>
          <div className="glass-effect p-4 rounded-xl">
            <Truck className="text-soya-yellow mx-auto mb-2" size={24} />
            <h3 className="text-white font-semibold">Livraison 5€</h3>
            <p className="text-gray-300 text-sm">Cugnaux et environs</p>
          </div>
          <div className="glass-effect p-4 rounded-xl">
            <Star className="text-soya-yellow mx-auto mb-2" size={24} />
            <h3 className="text-white font-semibold">5/5 étoiles</h3>
            <p className="text-gray-300 text-sm">Nos clients adorent</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const CustomizeModal = ({ item, onClose, onAddToCart }) => {
  const [selectedSauce, setSelectedSauce] = useState(SAUCES[1]); // Default to Algérienne
  const [extraSauces, setExtraSauces] = useState([]);
  const [isMenuOption, setIsMenuOption] = useState(false);
  const [selectedBoisson, setSelectedBoisson] = useState(BOISSONS[0]); // Default to Coca

  const handleExtraSauceToggle = (sauce) => {
    setExtraSauces(prev => {
      const exists = prev.find(s => s.id === sauce.id);
      if (exists) {  
        return prev.filter(s => s.id !== sauce.id);
      } else {
        return [...prev, sauce];
      }
    });
  };

  const calculateTotal = () => {
    let total = item.price;
    
    // Add extra sauces cost
    total += extraSauces.length * 0.50;
    
    // Add menu option cost
    if (isMenuOption) {
      total += 2.00; // Menu = +2€ (frites + boisson)
    }
    
    return total;
  };

  const handleAddToCart = () => {
    const customizedItem = {
      ...item,
      id: `${item.id}_${Date.now()}`, // Unique ID for customized items
      customization: {
        sauce: selectedSauce,
        extraSauces,
        isMenu: isMenuOption,
        boisson: isMenuOption ? selectedBoisson : null
      },
      price: calculateTotal(),
      // Use menu image if menu option is selected
      image: isMenuOption && item.menuImage ? item.menuImage : item.image,
      customDescription: `${item.name}${isMenuOption ? ' (Menu)' : ''} - Sauce: ${selectedSauce.name}${extraSauces.length > 0 ? ` + ${extraSauces.map(s => s.name).join(', ')}` : ''}${isMenuOption ? ` + Frites + ${selectedBoisson.name}` : ''}`
    };

    onAddToCart(customizedItem);
    onClose();
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-gray-800 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-2xl font-bold text-soya-yellow mb-2">{item.name}</h3>
            <p className="text-gray-300 text-sm">{item.description}</p>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Product Image with Menu Option Visual */}
        <div className="w-full h-48 mb-6 rounded-xl overflow-hidden relative">
          <img 
            src={isMenuOption && item.menuImage ? item.menuImage : item.image} 
            alt={item.name}
            className="w-full h-full object-cover transition-all duration-300"
          />
          {isMenuOption && (
            <div className="absolute top-2 right-2 bg-soya-red text-white px-2 py-1 rounded-full text-xs font-bold">
              MENU
            </div>
          )}
        </div>

        {/* Menu Option */}
        <div className="mb-6">
          <div className="flex items-center justify-between p-4 bg-gray-700 rounded-xl">
            <div>
              <h4 className="text-white font-semibold">🍟 Faire un menu (+2€)</h4>
              <p className="text-gray-300 text-sm">Ajouter frites + boisson</p>
            </div>
            <button
              onClick={() => setIsMenuOption(!isMenuOption)}
              className={`w-12 h-6 rounded-full transition-all duration-300 ${
                isMenuOption ? 'bg-soya-red' : 'bg-gray-600'
              }`}
            >
              <div className={`w-4 h-4 bg-white rounded-full transition-transform duration-300 ${
                isMenuOption ? 'translate-x-7' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>

        {/* Boisson Selection (only if menu option is selected) */}
        {isMenuOption && (
          <div className="mb-6">
            <h4 className="text-white font-semibold mb-3">🥤 Choisir votre boisson</h4>
            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto">
              {BOISSONS.map((boisson) => (
                <button
                  key={boisson.id}
                  onClick={() => setSelectedBoisson(boisson)}
                  className={`p-2 rounded-lg text-sm transition-all ${
                    selectedBoisson.id === boisson.id
                      ? 'bg-soya-red text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {boisson.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Sauce Selection */}
        <div className="mb-6">
          <h4 className="text-white font-semibold mb-3">🌶️ Choisir votre sauce (inclue)</h4>
          <div className="grid grid-cols-2 gap-2">
            {SAUCES.map((sauce) => (
              <button
                key={sauce.id}
                onClick={() => setSelectedSauce(sauce)}
                className={`p-3 rounded-lg text-sm transition-all flex items-center justify-between ${
                  selectedSauce.id === sauce.id
                    ? 'bg-soya-red text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <span>{sauce.name}</span>
                {sauce.popular && <span className="text-xs bg-soya-yellow text-black px-1 rounded">POP</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Extra Sauces */}
        <div className="mb-6">
          <h4 className="text-white font-semibold mb-3">➕ Sauces supplémentaires (+0.50€ chacune)</h4>
          <div className="grid grid-cols-2 gap-2">
            {SAUCES.filter(sauce => sauce.id !== 'sans-sauce' && sauce.id !== selectedSauce.id).map((sauce) => (
              <button
                key={sauce.id}
                onClick={() => handleExtraSauceToggle(sauce)}
                className={`p-2 rounded-lg text-sm transition-all ${
                  extraSauces.find(s => s.id === sauce.id)
                    ? 'bg-soya-yellow text-black'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {sauce.name} (+0.50€)
              </button>
            ))}
          </div>
        </div>

        {/* Total and Add Button */}
        <div className="flex items-center justify-between">
          <div className="text-right">
            <p className="text-gray-300 text-sm">Total</p>
            <p className="text-2xl font-bold text-soya-red ml-2">{calculateTotal().toFixed(2)}€</p>
          </div>
          <motion.button
            onClick={handleAddToCart}
            className="bg-gradient-to-r from-soya-red to-pink-600 hover:from-soya-red/80 hover:to-pink-600/80 text-white px-8 py-3 rounded-xl font-semibold transition-all flex items-center space-x-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <ShoppingCart size={20} />
            <span>Ajouter au panier</span>
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const MenuSection = () => {
  const [activeCategory, setActiveCategory] = useState('burgers');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const { addToCart } = useCart();

  const categories = {
    burgers: { name: 'Burgers', items: menuItems.burgers, icon: <ChefHat size={20} /> },
    sandwiches: { name: 'Sandwiches', items: menuItems.sandwiches, icon: <Heart size={20} /> },
    kids: { name: 'Menu Enfant', items: menuItems.kids, icon: <Users size={20} /> },
    petiteFaim: { name: 'Petite Faim', items: menuItems.petiteFaim, icon: <Zap size={20} /> },
    sides: { name: 'Accompagnements', items: menuItems.sides, icon: <Gift size={20} /> },
    boissons: { name: 'Boissons', items: menuItems.boissons, icon: <ArrowRight size={20} /> }
  };

  const handleAddToCart = (item) => {
    // Si c'est un burger ou sandwich, ouvrir le modal de personnalisation
    if (item.category === 'burgers' || item.category === 'sandwiches') {
      setSelectedItem(item);
      setShowCustomizeModal(true);
    } else {
      // Sinon, ajouter directement au panier
      addToCart(item);
    }
  };

  const ProductCard = ({ item }) => (
    <motion.div
      className="menu-card rounded-2xl p-6 hover:shadow-2xl relative overflow-hidden"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -5 }}
    >
      {/* Badges */}
      <div className="absolute top-4 right-4 flex flex-col space-y-1">
        {item.popular && (
          <div className="bg-soya-red text-white px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
            <TrendingUp size={12} />
            <span>POPULAIRE</span>
          </div>
        )}
        {item.new && (
          <div className="bg-soya-yellow text-black px-2 py-1 rounded-full text-xs font-bold">
            NOUVEAU
          </div>
        )}
        {item.spicy && (
          <div className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold">
            🌶️ ÉPICÉ
          </div>
        )}
      </div>

      {/* Product Image avec LazyImage */}
      <div className="w-full h-48 mb-4 rounded-xl overflow-hidden">
        <LazyImage 
          src={item.image} 
          alt={item.name}
          className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
        />
      </div>

      {/* Product Info */}
      <div className="mb-4">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-xl font-bold text-soya-yellow">{item.name}</h3>
          <div className="text-right">
            {item.originalPrice && (
              <span className="text-gray-500 line-through text-sm">{item.originalPrice.toFixed(2)}€</span>
            )}
            <span className="text-2xl font-bold text-soya-red ml-2">{item.price.toFixed(2)}€</span>
          </div>
        </div>
        
        {item.description && (
          <p className="text-gray-300 mb-4 text-sm leading-relaxed">{item.description}</p>
        )}
      </div>

      {/* Add to Cart Button */}
      <motion.button
        onClick={() => handleAddToCart(item)}
        className="w-full bg-gradient-to-r from-soya-red to-pink-600 hover:from-soya-red/80 hover:to-pink-600/80 text-white py-3 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 shadow-lg"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Plus size={20} />
        <span>
          {(item.category === 'burgers' || item.category === 'sandwiches') 
            ? 'Personnaliser' 
            : 'Ajouter au panier'
          }
        </span>
      </motion.button>
    </motion.div>
  );

  return (
    <section id="menu" className="py-20 bg-gray-900">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl font-display font-black text-white mb-4 neon-glow">
            Notre Menu
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Des saveurs authentiques préparées avec des ingrédients frais
          </p>
        </motion.div>

        {/* Category Navigation */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {Object.entries(categories).map(([key, category]) => (
            <motion.button
              key={key}
              onClick={() => setActiveCategory(key)}
              className={`px-6 py-3 rounded-full font-semibold transition-all flex items-center space-x-2 ${
                activeCategory === key
                  ? 'bg-soya-red text-white shadow-lg'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category.icon}
              <span>{category.name}</span>
            </motion.button>
          ))}
        </div>

        {/* Menu Items */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          key={activeCategory}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {categories[activeCategory].items.map((item, index) => (
            <ProductCard key={item.id} item={item} />
          ))}
        </motion.div>

        {/* Special Offers */}
        
      </div>

      {/* Customize Modal */}
      <AnimatePresence>
        {showCustomizeModal && selectedItem && (
          <CustomizeModal 
            item={selectedItem} 
            onClose={() => {
              setShowCustomizeModal(false);
              setSelectedItem(null);
            }}
            onAddToCart={addToCart}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

const DeliverySection = () => {
  return (
    <section id="livraison" className="py-20 bg-gray-800">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl font-display font-black text-white mb-6 neon-glow">
              Livraison Rapide
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Nous livrons dans tout Cugnaux et ses alentours pour seulement 5€
            </p>
            
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="bg-soya-red p-3 rounded-full">
                                   <MapPin className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Zone de livraison</h3>
                  <p className="text-gray-300">Cugnaux et environs proches</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="bg-soya-yellow p-3 rounded-full">
                  <Truck className="text-black" size={24} />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Frais de livraison</h3>
                  <p className="text-gray-300">5€ partout dans notre zone</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="bg-soya-red p-3 rounded-full">
                  <Clock className="text-white" size={24} />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Délai de livraison</h3>
                  <p className="text-gray-300">30-45 minutes en moyenne</p>
                </div>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="relative rounded-2xl overflow-hidden">
              <img 
                src={heroImage} 
                alt="Livraison" 
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ContactSection = () => {
  return (
    <section id="contact" className="py-20 bg-gray-900">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl font-display font-black text-white mb-4 neon-glow">
            Contactez-nous
          </h2>
          <p className="text-xl text-gray-300">
            Prêt à commander ? Nous sommes là pour vous !
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="glass-effect rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-soya-yellow mb-6">Nos coordonnées</h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="text-soya-red mt-1" size={24} />
                  <div>
                    <h4 className="text-white font-semibold mb-1">Adresse</h4>
                    <p className="text-gray-300">31bis Av. de Toulouse, 31270 Cugnaux</p>
                    <p className="text-gray-400 text-sm mt-1">
                      <a
                        href="https://g.co/kgs/LrCSaJB"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-soya-yellow transition-colors flex items-center"
                      >
                        Voir sur Google Maps <ExternalLink size={14} className="ml-1" />
                      </a>
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <Phone className="text-soya-red mt-1" size={24} />
                  <div>
                    <h4 className="text-white font-semibold mb-1">Téléphone</h4>
                    <a href="tel:0567062916" className="text-gray-300 hover:text-soya-yellow transition-colors">
                      05 67 06 29 16
                    </a>
                    <p className="text-gray-400 text-sm mt-1">
                      Du mardi au dimanche de 20h00 à 02h00
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="glass-effect rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-soya-yellow mb-6">Commandez maintenant</h3>
              
              <div className="space-y-4">
                <motion.a
                  href="https://wa.me/33618364907"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whatsapp-button w-full p-4 rounded-xl text-white font-semibold flex items-center justify-center space-x-3"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <MessageCircle size={24} />
                  <span>Commander via WhatsApp</span>
                </motion.a>
                
                <motion.a
                  href="tel:0567062916"
                  className="bg-soya-red hover:bg-soya-red/80 w-full p-4 rounded-xl text-white font-semibold flex items-center justify-center space-x-3 transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Phone size={24} />
                  <span>Appeler maintenant</span>
                </motion.a>
              </div>
            </div>

            <div className="glass-effect rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-soya-yellow mb-4">Nos avis clients</h3>
              <div className="flex items-center space-x-2 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="text-soya-yellow fill-current" size={20} />
                ))}
                <span className="text-gray-300 ml-2">5/5</span>
              </div>
              <a 
                href="https://www.google.com/search?sca_esv=dbfdd0f0cf0fea7e&rlz=1C1CHBF_frFR927FR927&tbm=lcl&q=%F0%9F%A5%AA%F0%9F%8D%94+Soya-Food+%E2%80%93+Snack+de+nuit+%C3%A0+Cugnaux+Avis&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxIxNDcyNjIxNTE2MDMyNTQ0MjcwMdvAyPiK0eTD_KWrPszvnaIQnF-ZqOuWn5-i8KhhskJwXmJytkJKqkJeaWaJwuEFCs6l6XmJpRUKjmWZxYtYydIGAFxG6I2JAAAA&rldimm=17232454306251127046&hl=fr-FR&sa=X&ved=2ahUKEwikweOo-t2OAxVTcKQEHeyyEdwQ9fQKegQIRhAF&biw=2048&bih=983&dpr=1.25#lkt=LocalPoiReviews"
                target="_blank"
                rel="noopener noreferrer"
                className="text-soya-yellow hover:text-soya-yellow/80 transition-colors flex items-center space-x-2"
              >
                <span>Voir tous les avis</span>
                <ExternalLink size={16} />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-black py-12 relative">
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-10"
        style={{
          backgroundImage: `url(${FOOTER_IMAGE})`
        }}
      />
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
          <div>
            <div className="flex items-center justify-center md:justify-start space-x-3 mb-4">
              <img src={LOGO_URL} alt="Soya-Food" className="h-12 w-12" />
              <div>
                <h3 className="text-xl font-bold text-soya-yellow">Soya-Food</h3>
                <p className="text-gray-400">Snack de nuit</p>
              </div>
            </div>
            <p className="text-gray-400">
              Votre snack de nuit préféré à Cugnaux. Des saveurs authentiques, 
              une livraison rapide et un service de qualité.
            </p>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-4">Liens utiles</h4>
            <ul className="space-y-2 text-gray-400">
              <li><a href="#menu" className="hover:text-soya-yellow transition-colors">Menu</a></li>
              <li><a href="#livraison" className="hover:text-soya-yellow transition-colors">Livraison</a></li>
              <li><a href="#contact" className="hover:text-soya-yellow transition-colors">Contact</a></li>
              <li><a href="#cgu" className="hover:text-soya-yellow transition-colors">CGU</a></li>
              <li><a href="#mentions-legales" className="hover:text-soya-yellow transition-colors">Mentions légales</a></li>
              <li><a href="#politique-confidentialite" className="hover:text-soya-yellow transition-colors">Confidentialité</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <div className="space-y-2 text-gray-400">
              <p>31bis Av. de Toulouse</p>
              <p>31270 Cugnaux</p>
              <p>05 67 06 29 16</p>
            </div>
          </div>
        </div>
        
        <div className="section-divider my-8" />
        
        <div className="text-center text-gray-400">
          <p>&copy; 2024 Soya-Food Cugnaux. Tous droits réservés. Site conforme RGPD.</p>
        </div>
      </div>
    </footer>
  );
};
const SEOHelmet = () => null;
const Home = () => {
  const [currentPage, setCurrentPage] = useState('home');

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      setCurrentPage(hash || 'home');
    };

    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();

    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const renderPage = () => {
    switch(currentPage) {
      case 'mentions-legales':
        return <MentionsLegales />;
      case 'politique-confidentialite':
        return <PolitiqueConfidentialite />;
      case 'cgu':
        return <CGUSection />;
      default:
        return (
          <>
            <HeroSection />
            <MenuSection />
            <DeliverySection />
            <ContactSection />
          </>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <SEOHelmet />
      <Header />
      <Cart />
      <CookieBanner />
      
      <div className={currentPage !== 'home' ? 'pt-20' : ''}>
        {renderPage()}
      </div>
      
      <Footer />
      
      {/* Fixed WhatsApp Button */}
      <motion.a
        href="https://wa.me/33618364907"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 whatsapp-button p-4 rounded-full z-40 shadow-2xl"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 2 }}
      >
        <MessageCircle className="text-white" size={28} />
      </motion.a>
    </div>
  );
};

function App() {
  // Ajoute la classe overflow-x-hidden au body pour éviter le scroll horizontal
  useEffect(() => {
    document.body.classList.add('overflow-x-hidden');
    return () => {
      document.body.classList.remove('overflow-x-hidden');
    };
  }, []);

  // Ajoute un padding-top dynamique pour compenser le header fixé sur mobile
  const [headerOffset, setHeaderOffset] = useState(0);
  useEffect(() => {
    const updateOffset = () => {
      // Si mobile (largeur < 768px), header ~72px, sinon 0
      setHeaderOffset(window.innerWidth < 768 ? 72 : 0);
    };
    updateOffset();
    window.addEventListener('resize', updateOffset);
    return () => window.removeEventListener('resize', updateOffset);
  }, []);

  return (
    <CartProvider>
      <div className="App min-h-screen bg-gray-900">
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#1a1a1a',
              color: '#fff',
              border: '1px solid #E91E63'
            }
          }}
        />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={
              // Ajoute le padding-top ici pour compenser le header fixé
              <div style={{ paddingTop: headerOffset }}>
                <Home />
              </div>
            } />
          </Routes>
        </BrowserRouter>
      </div>
    </CartProvider>
  );
}

export default App;